import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApplicationFilterState } from "../components/ApplicationsFilter/ApplicationsFilter";
import { ErrorToast, SuccessToast } from "../components/toastMessage";
import { _axios } from "../utils/axios";
import {
  TApplicationField,
  TApplicationFieldOption,
} from "./application-fields.hooks";
import { TDocumentConfig, TForm } from "./forms.hooks";

export type TApplication = {
  id: number;
  userId: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
  applicationId: string;
  project_name?: string;
  plot_owner_s_name?: string;
};

export const levelThreeUserApprovalKeyStore = {
  declaration: ["declaration"],
  basicForm: ["base-form"],
  id: (arg: number | string) => ["level-3-user-approvals", arg],
  counts: (...args: (number | string | Record<string, unknown>)[]) => [
    "level-3-user-approvals-counts",
    ...args,
  ],
  list: (...args: (number | string | Record<string, unknown>)[]) => [
    "level-3-user-approvals",
    ...args,
  ]
};

export function useLevelThreeUserApprovalsCount({
  search,
  filters,
}: {
  search?: string;
  filters?: any;
}) {
  return useQuery({
    queryKey: levelThreeUserApprovalKeyStore.counts(search || "", filters || {}),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: number;
      }>("/level-3-user-approvals/counts", { search, filters }),
    staleTime: 10 * 1000,
    select: (d) => d.data,
  });
}

export function useLevelThreeUserApprovals({
  page,
  limit,
  search,
  sort,
  sortVal,
  filters,
}: {
  page: number;
  limit: number;
  search?: string;
  sort: string;
  sortVal: number;
  filters?: any;
}) {
  return useQuery({
    queryKey: levelThreeUserApprovalKeyStore.list(
      page,
      limit,
      sort,
      sortVal,
      search || "",
      filters || {}
    ),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: any[];
      }>("/level-3-user-approvals", {
        sort,
        sortVal,
        search,
        page,
        limit,
        filters: filters || {},
      }),
    staleTime: 10 * 1000,
    select: (d) => d.data,
  });
}

export type TPopulatedForm = TForm & {
  documentConfigs?: TDocumentConfig[];
  /**
   * Documents mapped to this form but disabled in Config Admin. Never asked
   * for; only used to label uploads made before the document was disabled.
   */
  disabledDocumentConfigs?: TDocumentConfig[];
  fields?: (TApplicationField & {
    options?: TApplicationFieldOption[];
  })[];
};

export function useLevelThreeUserApproval({ id }: { id: number | string }) {
  return useQuery({
    queryKey: levelThreeUserApprovalKeyStore.id(id),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: any & Record<string, unknown>;
      }>(`/level-3-applications/${id}`),
    select: (d) => d?.data?.data,
    enabled: !!id,
  });
}
